'use client';

import { ArrowRight } from '@drift-labs/icons';
import { useEffect } from 'react';
import { UserSettings } from 'src/environmentVariables/EnvironmentVariables';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import { useSettingsContext } from 'src/providers/settingsProvider';
import { DriftStore } from 'src/stores/DriftStore/useDriftStore';
import {
	CallbackAction,
	NotificationData,
	getTimeoutNotificationData,
	notify,
} from 'src/utils/notifications';
import useNotificationStore from '../../stores/useNotificationStore';
import Checkbox from '../primitives/Checkbox/Checkbox';
import NotificationLink from './NotificationLink';
import Toast from './Toast';
import invariant from 'tiny-invariant';
import { cj } from 'src/utils/classNames';
import {
	TOAST_CONTENT_CONTAINER_CLASSNAME,
	TOAST_CONTENT_CONTAINER_STYLE,
	TOAST_SUB_CONTENT_CLASSNAME,
} from 'src/components/Toasts/consts';

const TIMEOUT_FAILSAFE_MS = 50_000;

const Notification = ({
	index,
	notificationData,
	statusWarning,
	statusType,
}: {
	index: number;
	notificationData: NotificationData;
	statusWarning: DriftStore['driftStatus']['mainStatus']['message'];
	statusType: DriftStore['driftStatus']['mainStatus']['status'];
}) => {
	const notificationStore = useNotificationStore();
	const settingsContext = useSettingsContext();
	const appEventEmitter = useAppEventEmitter();

	const targetIgnoreToastEnabled =
		!!notificationData.offerIgnoreToast &&
		settingsContext.currentSettings.ignoreToasts[
			notificationData.offerIgnoreToast
		];

	const handleToggleTargetIgnoreToast = () => {
		if (!notificationData.offerIgnoreToast) {
			return;
		}

		settingsContext.settingsClient?.updateSettings({
			ignoreToasts: {
				...settingsContext.currentSettings.ignoreToasts,
				[notificationData.offerIgnoreToast]:
					!settingsContext.settingsClient.overallSettingsState[
						notificationData.offerIgnoreToast as keyof UserSettings
					],
			},
		});
	};

	const closeNotification = () => {
		notificationStore.removeNotificationByIndex(index);
		notificationData.onDismiss?.();
	};

	const setHovered = () => {
		notificationStore.updateNotificationByIndex(index, {
			isHovered: true,
		});
	};

	const unsetHovered = () => {
		notificationStore.updateNotificationByIndex(index, {
			isHovered: false,
		});
	};

	const {
		message,
		description,
		subDescription,
		steps,
		timeoutIfProlongedTxn: timeoutForProlongedTxnFailSafe,
		timeoutStartMs,
		timeoutEndMs,
	} = notificationData;

	// this is a failsafe to ensure that if a transaction is taking too long, we update
	// the toast to inform the user that the transaction may have timed out
	useEffect(() => {
		if (timeoutForProlongedTxnFailSafe) {
			const timeout = setTimeout(() => {
				notify({
					...notificationData,
					...getTimeoutNotificationData(appEventEmitter),
					timeoutIfProlongedTxn: false,
				});
			}, TIMEOUT_FAILSAFE_MS);
			return () => clearTimeout(timeout);
		}
	}, [timeoutForProlongedTxnFailSafe]);

	const notificationType = notificationData.bgType
		? notificationData.bgType
		: notificationData.type;

	invariant(
		notificationType !== 'alert',
		'Notification type cannot be "alert" - should be rendering an AlertToast instead'
	);

	return (
		<Toast
			onClose={closeNotification}
			onMouseEnter={setHovered}
			onMouseLeave={unsetHovered}
			type={notificationType}
			timeoutStartMs={timeoutStartMs}
			timeoutEndMs={timeoutEndMs}
		>
			<div
				className={TOAST_CONTENT_CONTAINER_CLASSNAME}
				style={TOAST_CONTENT_CONTAINER_STYLE}
			>
				{message}

				{description && (
					<span className={TOAST_SUB_CONTENT_CLASSNAME}>{description}</span>
				)}

				{subDescription && (
					<span className={TOAST_SUB_CONTENT_CLASSNAME}>{subDescription}</span>
				)}

				{steps}

				{notificationData.action && (
					<div
						onClick={(e) => {
							e.stopPropagation();
							(notificationData.action as CallbackAction).callback?.();
							closeNotification();
						}}
						className={cj(TOAST_SUB_CONTENT_CLASSNAME, 'cursor-pointer')}
					>
						{notificationData.action.type === 'link' ||
						notificationData.action.type === 'txnLink' ? (
							<NotificationLink linkData={notificationData.action} />
						) : notificationData.action.type === 'custom' ? (
							notificationData.action.content
						) : (
							<span
								className={cj(
									'flex items-center gap-x-xs transition-colors',
									'text-text-default hover:text-text-secondary'
								)}
							>
								<span>{notificationData.action.label}</span>
								<ArrowRight autoSize />
							</span>
						)}
					</div>
				)}

				{statusWarning && (
					<div
						className={cj(
							'flex items-center gap-x-xs',
							TOAST_SUB_CONTENT_CLASSNAME
						)}
					>
						<div className="relative flex shrink-0 items-center justify-center">
							<div
								className={cj(
									'size-sm rounded-full',
									statusType === 'green'
										? 'bg-text-positive-green-button'
										: statusType === 'amber'
											? 'bg-text-star-yellow'
											: 'bg-text-negative-red-button'
								)}
							/>
						</div>
						{statusWarning}
					</div>
				)}

				{notificationData.offerIgnoreToast && (
					<div
						className="flex items-start"
						onClick={(e) => {
							// Things like attached URLs work if any click event bubbles up, we want to prevent that for this part of the toast.
							e.preventDefault();
							e.stopPropagation();
						}}
					>
						<Checkbox.Default
							id="notification-dont-show-again"
							label="Don't show again"
							checked={targetIgnoreToastEnabled}
							onCheckedChange={handleToggleTargetIgnoreToast}
						/>
					</div>
				)}

				{notificationData.bottomContent}
			</div>
		</Toast>
	);
};

export default Notification;
